import {
  useRef,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

export interface HoverTooltipProps {
  /** Trigger element - tooltip shows on hover over this */
  children: ReactNode;
  /** Tooltip content (can include links and other React nodes) */
  content: ReactNode;
  /** Position relative to trigger */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Max width of tooltip */
  maxWidth?: string;
  /** Delay before showing (ms) */
  showDelay?: number;
  /** Delay before hiding (ms) */
  hideDelay?: number;
  /** Only show tooltip when this is true */
  disabled?: boolean;
}

const tooltipStyle = {
  backgroundColor: 'var(--bq-tooltip-bg)',
  border: '1px solid var(--bq-tooltip-border)',
  borderRadius: '6px',
  padding: '8px 12px',
  color: 'var(--bq-tooltip-text)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  zIndex: 9999,
} as const;

export const HoverTooltip: React.FC<HoverTooltipProps> = ({
  children,
  content,
  position = 'bottom',
  maxWidth = '320px',
  showDelay = 200,
  hideDelay = 100,
  disabled = false,
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (visible && triggerRef.current) {
      setPortalTarget(triggerRef.current.ownerDocument?.body ?? document.body);
    } else {
      setPortalTarget(null);
    }
  }, [visible]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const padding = 8;
    if (position === 'bottom') {
      setCoords({
        top: rect.bottom + padding,
        left: rect.left + rect.width / 2,
      });
    } else if (position === 'top') {
      setCoords({
        top: rect.top,
        left: rect.left + rect.width / 2,
      });
    } else if (position === 'left') {
      setCoords({
        top: rect.top + rect.height / 2,
        left: rect.left,
      });
    } else {
      setCoords({
        top: rect.top + rect.height / 2,
        left: rect.right + padding,
      });
    }
  }, [position]);

  const handleMouseEnter = useCallback(() => {
    if (disabled) return;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    showTimeoutRef.current = setTimeout(() => {
      setVisible(true);
      showTimeoutRef.current = null;
    }, showDelay);
  }, [disabled, showDelay]);

  const handleMouseLeave = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    hideTimeoutRef.current = setTimeout(() => {
      setVisible(false);
      hideTimeoutRef.current = null;
    }, hideDelay);
  }, [hideDelay]);

  useEffect(() => {
    if (visible && triggerRef.current) {
      updatePosition();
    }
  }, [visible, updatePosition]);

  useEffect(() => {
    if (!visible) return;
    const targetWindow =
      triggerRef.current?.ownerDocument?.defaultView ?? window;
    const onScrollOrResize = () => updatePosition();
    targetWindow.addEventListener('scroll', onScrollOrResize, true);
    targetWindow.addEventListener('resize', onScrollOrResize);
    return () => {
      targetWindow.removeEventListener('scroll', onScrollOrResize, true);
      targetWindow.removeEventListener('resize', onScrollOrResize);
    };
  }, [visible, updatePosition]);

  const transform =
    position === 'bottom'
      ? 'translate(-50%, 0)'
      : position === 'top'
        ? 'translate(-50%, -100%)'
        : position === 'left'
          ? 'translate(-100%, -50%)'
          : 'translate(0, -50%)';

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-block"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </span>
      {visible &&
        content != null &&
        content !== '' &&
        portalTarget &&
        createPortal(
          <div
            role="tooltip"
            className="fixed"
            style={{
              top: coords.top,
              left: coords.left,
              transform,
              maxWidth,
              ...tooltipStyle,
            }}
            onMouseEnter={() => {
              if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
              }
            }}
            onMouseLeave={handleMouseLeave}
          >
            {/* Arrow */}
            {position === 'bottom' && (
              <div
                className="absolute"
                style={{
                  top: '-6px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderBottom: '6px solid var(--bq-tooltip-border)',
                }}
              />
            )}
            {position === 'top' && (
              <div
                className="absolute"
                style={{
                  bottom: '-6px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '6px solid var(--bq-tooltip-border)',
                }}
              />
            )}
            <div
              className="text-sm"
              style={{ position: 'relative', zIndex: 1 }}
            >
              {content}
            </div>
          </div>,
          portalTarget,
        )}
    </>
  );
};
